/**
 * Derived answers (Tzvi ruling 2026-07-19): questions the Department asks but
 * the client should never see because the answer is mechanical.
 *
 * Usual country of residence (820/309): the lodged form needs it, but it is
 * always the country of the residential address the client already typed. The
 * field is client_hidden in the schema; this derivation runs inside the save
 * path (flushSave, the single writer), so the value lands in the saved
 * applicant blob whenever the residential address has a country. Staff see it
 * populated and can rely on it at transcription. A manual staff edit persists
 * until the residential country changes again (the mirror always wins then,
 * by design: it must never silently disagree with the address).
 *
 * Department office (820/309/300, Jane call feedback 2026-07-20): the client
 * answers Office in Personal details (applicant.department_office); the
 * Lodgement details copy (application.department_office) is read_only in the
 * schema and mirrored here so lodgement always shows what the client chose.
 *
 * Migrating-family over-5 (820/309/300, R2 Jane 2026-07-21): the client no
 * longer sees the "over 5 years of age?" question (it is client_hidden). The
 * answer is mechanical, so we derive it from each migrating member's date of
 * birth. Derivation only FILLS an empty value (never overwrites a stored one),
 * so a legacy or staff-entered answer is preserved. Staff and the PDF read the
 * value from the saved blob exactly like any other answer.
 *
 * Witness contact channels (820/309/300, R6 Jane 2026-07-21): the witness
 * phone/email fields are client_hidden. ADI's own contact details are seeded
 * into every named witness entry (fill-if-empty), sourced from adi-agent.ts so
 * there is a single copy of ADI's details. Staff can overwrite with a real
 * witness email; once set, the fill-if-empty rule leaves it alone.
 */
import { readNestedPath } from './formStateAdapter';
import type { FormState } from './formStateAdapter';
import { witnessContactDefaults } from '../../schema/adi-agent';

const UCR_FORMS = new Set(['47SP_820_onshore_stage1', '47SP_309_offshore_stage1']);
const OFFICE_FORMS = new Set(['47SP_820_onshore_stage1', '47SP_309_offshore_stage1', '47SP_300_PMV']);
const MIGRATING_FAMILY_FORMS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
  '47SP_300_PMV',
]);

/**
 * Whether a person born on `dob` (YYYY-MM-DD) is more than five years old at
 * `at`. Returns null when the date cannot be parsed, so the caller leaves the
 * answer untouched rather than guessing.
 */
export function isOverFiveAt(dob: unknown, at: Date): boolean | null {
  if (typeof dob !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const birth = new Date(year, month - 1, day);
  if (
    Number.isNaN(birth.getTime()) ||
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return null;
  }
  const fifthBirthday = new Date(year + 5, month - 1, day);
  return at.getTime() >= fifthBirthday.getTime();
}

export function applyDerivedAnswers(
  state: FormState,
  formId: string | null | undefined,
  now: Date = new Date(),
): FormState {
  if (!formId) return state;
  let next = state;
  if (UCR_FORMS.has(formId)) {
    const country = readNestedPath(next, 'applicant.residential_address.country');
    if (typeof country === 'string' && country.length > 0) {
      const current = readNestedPath(next, 'applicant.usual_country_of_residence');
      if (current !== country) {
        const applicant = {
          ...((next as Record<string, unknown>).applicant as Record<string, unknown> | undefined),
          usual_country_of_residence: country,
        };
        next = { ...next, applicant };
      }
    }
  }
  if (OFFICE_FORMS.has(formId)) {
    const office = readNestedPath(next, 'applicant.department_office');
    if (typeof office === 'string' && office.length > 0) {
      const current = readNestedPath(next, 'application.department_office');
      if (current !== office) {
        const application = {
          ...((next as Record<string, unknown>).application as Record<string, unknown> | undefined),
          department_office: office,
        };
        next = { ...next, application };
      }
    }
  }
  if (MIGRATING_FAMILY_FORMS.has(formId)) {
    const entries = readNestedPath(next, 'applicant.migrating_family.entries');
    if (Array.isArray(entries)) {
      let changed = false;
      const derivedEntries = entries.map((entry) => {
        if (!entry || typeof entry !== 'object') return entry;
        const e = entry as Record<string, unknown>;
        const current = e.is_over_5;
        if (typeof current === 'string' && current.length > 0) return entry;
        const derived = isOverFiveAt(e.dob, now);
        if (derived === null) return entry;
        changed = true;
        return { ...e, is_over_5: derived ? 'Yes' : 'No' };
      });
      if (changed) {
        const applicant = (next as Record<string, unknown>).applicant as
          | Record<string, unknown>
          | undefined;
        const migratingFamily = applicant?.migrating_family as Record<string, unknown> | undefined;
        next = {
          ...next,
          applicant: {
            ...applicant,
            migrating_family: { ...migratingFamily, entries: derivedEntries },
          },
        };
      }
    }
  }
  const witnessDefaults = witnessContactDefaults(formId);
  if (Object.keys(witnessDefaults).length > 0) {
    const witnesses = readNestedPath(next, 'applicant.witnesses');
    if (Array.isArray(witnesses)) {
      let changed = false;
      const seededWitnesses = witnesses.map((witness) => {
        if (!witness || typeof witness !== 'object') return witness;
        const w = witness as Record<string, unknown>;
        // Only seed real witnesses (ones the client has actually named), so a
        // blank repeater row is not populated with ADI's contact details.
        const familyName = w.family_name;
        if (typeof familyName !== 'string' || familyName.trim() === '') return witness;
        let entryChanged = false;
        const nextWitness = { ...w };
        for (const [key, value] of Object.entries(witnessDefaults)) {
          const current = nextWitness[key];
          if (current === undefined || current === null || current === '') {
            nextWitness[key] = value;
            entryChanged = true;
          }
        }
        if (!entryChanged) return witness;
        changed = true;
        return nextWitness;
      });
      if (changed) {
        const applicant = (next as Record<string, unknown>).applicant as
          | Record<string, unknown>
          | undefined;
        next = {
          ...next,
          applicant: { ...applicant, witnesses: seededWitnesses },
        };
      }
    }
  }
  return next;
}
